import type { ColumnDef } from '@tanstack/react-table';
import { Archive, Eye, Pencil } from 'lucide-react';
import * as React from 'react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { ProjectFormDialog } from './ProjectFormDialog';
import { useCloseProject, useDeactivateProject, useProjects } from './use-projects';
import type { Project } from '../../../types/admin';
import { textoSegunAcceso, useAccesoAdmin } from '../acceso-admin';
import { InmobiliariaSelector } from '../inmobiliaria-activa/inmobiliaria-activa';

const PAGE_SIZE = 20;

export function ProjectsPage(): React.JSX.Element {
  const acceso = useAccesoAdmin('proyecto');
  const [page, setPage] = React.useState(1);
  const [formOpen, setFormOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'view' | 'edit'>('edit');
  const [editing, setEditing] = React.useState<Project | null>(null);
  const [toClose, setToClose] = React.useState<Project | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useProjects(page, PAGE_SIZE);
  const deactivateProject = useDeactivateProject();
  const closeProject = useCloseProject();

  const canCreate = acceso.crear;
  const canEdit = acceso.editar;
  // change-70: ACTIVO se cierra con `close` (auditado, sin separaciones nuevas); SIN_CATALOGO, con el
  // DELETE de siempre (sin dependencias); CERRADO no ofrece la acción.
  const cierreActivo = toClose?.estado === 'ACTIVO';
  const cerrando = closeProject.isPending || deactivateProject.isPending;

  const columns = React.useMemo<Array<ColumnDef<Project, unknown>>>(
    () => [
      { accessorKey: 'codigo', header: 'Código' },
      { accessorKey: 'nombre', header: 'Nombre' },
      { accessorKey: 'direccion', header: 'Dirección' },
      { accessorKey: 'ciudad', header: 'Ciudad' },
      { accessorKey: 'matriculaMatriz', header: 'Matrícula' },
      {
        id: 'estado',
        header: 'Estado',
        cell: ({ row }) => <StatusBadge status={row.original.estado} />,
      },
    ],
    [],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Administración"
          title="Proyectos"
          description={textoSegunAcceso(acceso, {
            gestion: 'Administra los proyectos de la inmobiliaria: créalos, edítalos y ciérralos.',
            consulta: 'Consulta los proyectos de tu inmobiliaria y su estado.',
          })}
          action={
            canCreate ? (
              <Button
                onClick={() => {
                  setEditing(null);
                  setMode('edit');
                  setFormOpen(true);
                }}
              >
                Nuevo proyecto
              </Button>
            ) : undefined
          }
        />

        <InmobiliariaSelector />

        {feedback && (
          <p role="status" className="rounded-lg border border-border/70 bg-surface-fade px-4 py-3 text-sm">
            {feedback}
          </p>
        )}

        {isLoading ? (
          <LoadingState message="Cargando proyectos" />
        ) : isError ? (
          <ErrorState
            message="No fue posible cargar los proyectos"
            onRetry={() => void refetch()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            emptyMessage="No hay proyectos"
            manualPagination
            rowCount={data?.meta.total ?? 0}
            pagination={{ pageIndex: page - 1, pageSize: PAGE_SIZE }}
            onPaginationChange={(state) => setPage(state.pageIndex + 1)}
            rowActions={(project) => (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Ver ${project.nombre}`}
                      onClick={() => {
                        setEditing(project);
                        setMode('view');
                        setFormOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ver</TooltipContent>
                </Tooltip>
                {canEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Editar ${project.nombre}`}
                        onClick={() => {
                          setEditing(project);
                          setMode('edit');
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                )}
                {canEdit && project.estado !== 'CERRADO' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Cerrar ${project.nombre}`}
                        onClick={() => setToClose(project)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cerrar</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
          />
        )}

        <ProjectFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) {
              setEditing(null);
            }
          }}
          project={editing}
          mode={mode}
        />

        <ConfirmDialog
          open={Boolean(toClose)}
          onOpenChange={(open) => {
            if (!open) {
              setToClose(null);
            }
          }}
          title="Cerrar proyecto"
          description={
            toClose
              ? cierreActivo
                ? `El proyecto ${toClose.nombre} quedará cerrado: no admitirá separaciones nuevas ni cargas de catálogo y dejará de aparecer a los asesores. Las separaciones emitidas y su catálogo se conservan para consulta. En esta fase no se puede reabrir.`
                : `El proyecto ${toClose.nombre} quedará cerrado. Un proyecto sin catálogo solo se cierra si no tiene asesores asignados.`
              : undefined
          }
          confirmText="Cerrar proyecto"
          variant="destructive"
          loading={cerrando}
          onConfirm={() => {
            if (!toClose) {
              return;
            }
            const opciones = {
              onSuccess: () => {
                setFeedback(`El proyecto ${toClose.nombre} quedó cerrado.`);
                setToClose(null);
              },
              onError: (error: Error) => {
                setFeedback(error.message);
                setToClose(null);
              },
            };
            if (cierreActivo) {
              closeProject.mutate(toClose.id, opciones);
            } else {
              deactivateProject.mutate(toClose.id, opciones);
            }
          }}
        />
      </div>
    </TooltipProvider>
  );
}
